import { z } from 'zod';
import { UUIDSchema } from '../ids.js';
export const CallboxDevicePlacement = z.enum(['perimeter', 'interior']);
export const CallboxDirectoryGroupKind = z.enum([
    'perimeter',
    'interior',
    'tenant',
    'private',
    'admin',
]);
export const CallboxRingTargetType = z.enum(['tenant', 'admin_office', 'user', 'custom', 'none']);
export const CallboxDirectoryMemberKind = z.enum([
    'tenant',
    'household',
    'admin_office',
    'custom',
]);
export const CallboxSyncStatus = z.enum(['local_only', 'pending', 'synced', 'failed', 'unverified']);
const OptionalText = z.string().trim().max(500).nullable().optional();
const OptionalPhone = z.string().trim().max(40).nullable().optional();
export const CallboxDirectoryGroup = z.object({
    id: UUIDSchema,
    name: z.string(),
    description: z.string().nullable(),
    kind: CallboxDirectoryGroupKind,
    owner_tenant_id: UUIDSchema.nullable(),
    sync_status: CallboxSyncStatus,
    last_sync_error: z.string().nullable(),
    last_synced_at: z.string().datetime().nullable(),
    created_at: z.string().datetime(),
    updated_at: z.string().datetime(),
});
export const CreateCallboxGroupBody = z.object({
    name: z.string().trim().min(1).max(120),
    description: OptionalText,
    kind: CallboxDirectoryGroupKind.default('perimeter'),
    owner_tenant_id: UUIDSchema.nullable().optional(),
});
export const UpdateCallboxGroupBody = z.object({
    name: z.string().trim().min(1).max(120).optional(),
    description: OptionalText,
    kind: CallboxDirectoryGroupKind.optional(),
    owner_tenant_id: UUIDSchema.nullable().optional(),
});
export const CallboxDevice = z.object({
    id: UUIDSchema,
    building_resource_id: UUIDSchema.nullable(),
    directory_group_id: UUIDSchema,
    unifi_device_id: z.string().nullable(),
    name: z.string(),
    location_label: z.string().nullable(),
    placement: CallboxDevicePlacement,
    enabled: z.boolean(),
    ring_target_type: CallboxRingTargetType,
    ring_tenant_id: UUIDSchema.nullable(),
    ring_user_id: UUIDSchema.nullable(),
    ring_label: z.string().nullable(),
    ring_phone: z.string().nullable(),
    notes: z.string().nullable(),
    sync_status: CallboxSyncStatus,
    last_sync_error: z.string().nullable(),
    last_synced_at: z.string().datetime().nullable(),
    created_at: z.string().datetime(),
    updated_at: z.string().datetime(),
});
export const CreateCallboxDeviceBody = z.object({
    building_resource_id: UUIDSchema,
    directory_group_id: UUIDSchema,
    name: z.string().trim().min(1).max(120),
    location_label: z.string().trim().max(120).nullable().optional(),
    placement: CallboxDevicePlacement.default('perimeter'),
    enabled: z.boolean().default(true),
    ring_target_type: CallboxRingTargetType.default('admin_office'),
    ring_tenant_id: UUIDSchema.nullable().optional(),
    ring_user_id: UUIDSchema.nullable().optional(),
    ring_label: z.string().trim().max(120).nullable().optional(),
    ring_phone: OptionalPhone,
    notes: z.string().trim().max(2000).nullable().optional(),
});
export const UpdateCallboxDeviceBody = z.object({
    building_resource_id: UUIDSchema.optional(),
    directory_group_id: UUIDSchema.optional(),
    name: z.string().trim().min(1).max(120).optional(),
    location_label: z.string().trim().max(120).nullable().optional(),
    placement: CallboxDevicePlacement.optional(),
    enabled: z.boolean().optional(),
    ring_target_type: CallboxRingTargetType.optional(),
    ring_tenant_id: UUIDSchema.nullable().optional(),
    ring_user_id: UUIDSchema.nullable().optional(),
    ring_label: z.string().trim().max(120).nullable().optional(),
    ring_phone: OptionalPhone,
    notes: z.string().trim().max(2000).nullable().optional(),
});
export const AssignCallboxTenantBody = z.object({
    tenant_id: UUIDSchema,
    can_manage_directory: z.boolean().default(true),
    can_manage_ring_target: z.boolean().default(false),
    label_override: z.string().trim().max(120).nullable().optional(),
});
export const UpdateCallboxRingTargetBody = z.object({
    ring_target_type: CallboxRingTargetType,
    ring_tenant_id: UUIDSchema.nullable().optional(),
    ring_user_id: UUIDSchema.nullable().optional(),
    ring_label: z.string().trim().max(120).nullable().optional(),
    ring_phone: OptionalPhone,
});
export const ListCallboxDirectoryQuery = z.object({
    group_id: UUIDSchema.optional(),
    tenant_id: UUIDSchema.optional(),
});
export const CallboxDirectoryMember = z.object({
    id: UUIDSchema,
    directory_group_id: UUIDSchema,
    tenant_id: UUIDSchema.nullable(),
    kind: CallboxDirectoryMemberKind,
    display_name: z.string(),
    subtitle: z.string().nullable(),
    phone: z.string().nullable(),
    visibility: z.string(),
    target_tenant_id: UUIDSchema.nullable(),
    source_household_id: UUIDSchema.nullable(),
    routing_json: z.unknown().nullable().optional(),
    sort_order: z.number().int(),
    enabled: z.boolean(),
    sync_status: CallboxSyncStatus,
    last_sync_error: z.string().nullable(),
    last_synced_at: z.string().datetime().nullable(),
    created_at: z.string().datetime(),
    updated_at: z.string().datetime(),
});
export const CreateCallboxDirectoryMemberBody = z.object({
    directory_group_id: UUIDSchema,
    tenant_id: UUIDSchema.nullable().optional(),
    kind: CallboxDirectoryMemberKind.default('custom'),
    display_name: z.string().trim().min(1).max(120),
    subtitle: z.string().trim().max(120).nullable().optional(),
    phone: OptionalPhone,
    visibility: z.string().trim().min(1).max(40).default('public'),
    target_tenant_id: UUIDSchema.nullable().optional(),
    source_household_id: UUIDSchema.nullable().optional(),
    routing_json: z.unknown().nullable().optional(),
    sort_order: z.coerce.number().int().min(0).max(10000).default(0),
    enabled: z.boolean().default(true),
});
export const UpdateCallboxDirectoryMemberBody = z.object({
    directory_group_id: UUIDSchema.optional(),
    tenant_id: UUIDSchema.nullable().optional(),
    kind: CallboxDirectoryMemberKind.optional(),
    display_name: z.string().trim().min(1).max(120).optional(),
    subtitle: z.string().trim().max(120).nullable().optional(),
    phone: OptionalPhone,
    visibility: z.string().trim().min(1).max(40).optional(),
    target_tenant_id: UUIDSchema.nullable().optional(),
    source_household_id: UUIDSchema.nullable().optional(),
    routing_json: z.unknown().nullable().optional(),
    sort_order: z.coerce.number().int().min(0).max(10000).optional(),
    enabled: z.boolean().optional(),
});
//# sourceMappingURL=callbox.js.map